import { 
  collection, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { PROJECT_STATUS } from '../constants/projectConstants';
import { 
  calculateDepartmentTotals, 
  calculateSectorTotals,
  combineSessionData,
  calculateSessionProjectTotals,
  DEFAULT_SESSION_PROJECT
} from '../constants/sessionConstants';
import { sanitizeSessionProject } from '../utils/sessionValidation';

const SESSION_PROJECTS_COLLECTION = 'sessionProjects';

export const sessionService = {
  
  // Get approved projects for session budget
  async getApprovedProjects(financialYear = null, sector = null) {
    try {
      const projectsQuery = query(
        collection(db, 'projects'),
        where('status', '==', PROJECT_STATUS.APPROVED)
      );
      
      const querySnapshot = await getDocs(projectsQuery);
      let projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const project = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          approvedAt: data.approvedAt?.toDate(),
          clearanceDate: data.clearanceDate?.toDate(),
          clearanceUpdatedAt: data.clearanceUpdatedAt?.toDate()
        };
        projects.push(project);
      });
      
      if (financialYear) {
        projects = projects.filter(project => project.financialYear === financialYear);
      }
      
      if (sector) {
        projects = projects.filter(project => project.sector === sector);
      }
      
      projects.sort((a, b) => {
        if (a.department < b.department) return -1;
        if (a.department > b.department) return 1;
        return 0;
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching approved projects:', error);
      throw new Error('Failed to load approved projects: ' + error.message);
    }
  },

  // Get session budget data
  async getSessionBudgetData(financialYear, sector = null) {
    try {
      const projectsResult = await this.getApprovedProjects(financialYear, sector);
      if (!projectsResult.success) {
        throw new Error('Failed to fetch projects');
      }
      
      const projects = projectsResult.data;
      const departmentTotals = this.calculateCombinedSessionData(projects);
      const sectorTotals = calculateSectorTotals(departmentTotals);
      
      return {
        success: true,
        data: {
          departments: departmentTotals,
          sectorTotals,
          projects,
          totalProjects: projects.length
        }
      };
    } catch (error) {
      console.error('Error getting session budget data:', error);
      throw new Error('Failed to load session budget data: ' + error.message);
    }
  },

  // Calculate combined session data
  calculateCombinedSessionData(projects) {
    const departmentTotals = {};
    
    projects.forEach(project => {
      if (!project.department) return;
      
      if (!departmentTotals[project.department]) {
        departmentTotals[project.department] = {
          department: project.department,
          sector: project.sector,
          approvedTotal: 0,
          approvedStatePlan: 0,
          approvedNegotiatedLoan: 0,
          clearanceTotal: 0,
          clearanceStatePlan: 0,
          clearanceNegotiatedLoan: 0
        };
      }
      
      const dept = departmentTotals[project.department];
      const approvedAmount = parseFloat(project.financialRequirement) || 0;
      dept.approvedTotal += approvedAmount;
      
      if (project.clearanceType === 'state_plan') {
        dept.approvedStatePlan += approvedAmount;
      } else if (project.clearanceType === 'negotiated_loan') {
        dept.approvedNegotiatedLoan += approvedAmount;
      }
      
      if (project.allocatedAmount) {
        const allocatedAmount = parseFloat(project.allocatedAmount) || 0;
        dept.clearanceTotal += allocatedAmount;
        
        if (project.clearanceType === 'state_plan') {
          dept.clearanceStatePlan += allocatedAmount;
        } else if (project.clearanceType === 'negotiated_loan') {
          dept.clearanceNegotiatedLoan += allocatedAmount;
        }
      }
    });
    
    return Object.values(departmentTotals);
  },

  // Get session projects
  async getSessionProjects(financialYear = null, department = null) {
    try {
      let sessionQuery = collection(db, SESSION_PROJECTS_COLLECTION);
      const constraints = [];
      
      if (financialYear) constraints.push(where('financialYear', '==', financialYear));
      if (department) constraints.push(where('department', '==', department));
      
      constraints.push(orderBy('department', 'asc'));
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) sessionQuery = query(sessionQuery, ...constraints);
      
      const querySnapshot = await getDocs(sessionQuery);
      const sessionProjects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sessionProjects.push({
          id: doc.id,
          ...data,
          source: data.source || '',
          projectStatus: data.projectStatus || 'new_project',
          
          // Project locks
          isProjectLocked: data.isProjectLocked || false,
          projectLockedAt: data.projectLockedAt?.toDate() || null,
          projectLockedBy: data.projectLockedBy || null,
          projectLockedByName: data.projectLockedByName || null,
          
          // Clearance locks
          isClearanceLocked: data.isClearanceLocked || false,
          clearanceLockedAt: data.clearanceLockedAt?.toDate() || null,
          clearanceLockedBy: data.clearanceLockedBy || null,
          clearanceLockedByName: data.clearanceLockedByName || null,
          
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          createdBy: data.createdBy || null,
          updatedBy: data.updatedBy || null
        });
      });
      
      return { success: true, data: sessionProjects };
    } catch (error) {
      console.error('Error fetching session projects:', error);
      throw new Error('Failed to load session projects: ' + error.message);
    }
  },

  // PROJECT LOCK: Lock a session project (for SessionBudgetDescriptive)
  async lockSessionProject(projectId, userId, userName) {
    try {
      if (!projectId || !userId) {
        throw new Error('Project ID and user ID are required');
      }

      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      if (projectData.isProjectLocked) {
        throw new Error('Project is already locked');
      }

      const lockData = {
        isProjectLocked: true,
        projectLockedAt: serverTimestamp(),
        projectLockedBy: userId,
        projectLockedByName: userName || 'User',
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(projectRef, lockData);
      console.log('Session project locked:', projectId);
      return { success: true, message: 'Project locked successfully', data: lockData };
    } catch (error) {
      console.error('Error locking session project:', error);
      throw new Error(error.message || 'Failed to lock project');
    }
  },

  // PROJECT UNLOCK: Unlock a session project (for SessionBudgetDescriptive)
  async unlockSessionProject(projectId, userId, userName) {
    try {
      if (!projectId || !userId) {
        throw new Error('Project ID and user ID are required');
      }

      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      if (!projectData.isProjectLocked) {
        throw new Error('Project is not locked');
      }

      const unlockData = {
        isProjectLocked: false,
        projectLockedAt: null,
        projectLockedBy: null,
        projectLockedByName: null,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(projectRef, unlockData);
      console.log('Session project unlocked:', projectId);
      return { success: true, message: 'Project unlocked successfully', data: unlockData };
    } catch (error) {
      console.error('Error unlocking session project:', error);
      throw new Error(error.message || 'Failed to unlock project');
    }
  },

  // CLEARANCE LOCK: Lock clearance data (for ClearanceProjectWise)
  async lockSessionClearance(projectId, userId, userName) {
    try {
      if (!projectId || !userId) {
        throw new Error('Project ID and user ID are required');
      }

      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      if (projectData.isClearanceLocked) {
        throw new Error('Clearance data is already locked');
      }

      const lockData = {
        isClearanceLocked: true,
        clearanceLockedAt: serverTimestamp(),
        clearanceLockedBy: userId,
        clearanceLockedByName: userName || 'User',
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(projectRef, lockData);
      console.log('Session clearance locked:', projectId);
      return { success: true, message: 'Clearance locked successfully', data: lockData };
    } catch (error) {
      console.error('Error locking session clearance:', error);
      throw new Error(error.message || 'Failed to lock clearance');
    }
  },

  // CLEARANCE UNLOCK: Unlock clearance data (for ClearanceProjectWise)
  async unlockSessionClearance(projectId, userId, userName) {
    try {
      if (!projectId || !userId) {
        throw new Error('Project ID and user ID are required');
      }

      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      const projectData = projectDoc.data();
      if (!projectData.isClearanceLocked) {
        throw new Error('Clearance data is not locked');
      }

      const unlockData = {
        isClearanceLocked: false,
        clearanceLockedAt: null,
        clearanceLockedBy: null,
        clearanceLockedByName: null,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(projectRef, unlockData);
      console.log('Session clearance unlocked:', projectId);
      return { success: true, message: 'Clearance unlocked successfully', data: unlockData };
    } catch (error) {
      console.error('Error unlocking session clearance:', error);
      throw new Error(error.message || 'Failed to unlock clearance');
    }
  },

  // Add session project
  async addSessionProject(projectData, userId) {
    try {
      const sanitizedData = sanitizeSessionProject(projectData);
      
      const dataToSave = {
        ...DEFAULT_SESSION_PROJECT,
        ...sanitizedData,
        source: sanitizedData.source || '',
        projectStatus: sanitizedData.projectStatus || 'new_project',
        financialYear: sanitizedData.financialYear,
        isProjectLocked: false,
        isClearanceLocked: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      };
      
      const docRef = await addDoc(collection(db, SESSION_PROJECTS_COLLECTION), dataToSave);
      console.log('Session project added with ID:', docRef.id);
      return { success: true, data: { id: docRef.id, ...dataToSave } };
    } catch (error) {
      console.error('Error adding session project:', error);
      throw new Error(error.message || 'Failed to add session project');
    }
  },

  // Update session project - with project lock check
  async updateSessionProject(projectId, projectData, userId) {
    try {
      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const currentData = projectDoc.data();
        if (currentData.isProjectLocked) {
          throw new Error('Cannot update project: This project is locked. Contact admin to unlock.');
        }
      }
      
      const sanitizedData = sanitizeSessionProject(projectData);
      const updateData = {
        ...sanitizedData,
        source: sanitizedData.source || '',
        projectStatus: sanitizedData.projectStatus || 'new_project',
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };
      
      await updateDoc(projectRef, updateData);
      console.log('Session project updated:', projectId);
      return { success: true, data: updateData };
    } catch (error) {
      console.error('Error updating session project:', error);
      throw new Error(error.message || 'Failed to update session project');
    }
  },

  // Delete session project - with project lock check
  async deleteSessionProject(projectId) {
    try {
      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        if (projectData.isProjectLocked) {
          throw new Error('Cannot delete project: This project is locked. Contact admin to unlock.');
        }
      }
      
      await deleteDoc(projectRef);
      console.log('Session project deleted:', projectId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting session project:', error);
      throw new Error(error.message || 'Failed to delete session project');
    }
  },

  // Update session project clearance - with clearance lock check
  async updateSessionProjectClearance(projectId, clearanceData, userId) {
    try {
      const projectRef = doc(db, SESSION_PROJECTS_COLLECTION, projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        if (projectData.isClearanceLocked) {
          throw new Error('Cannot update clearance: Clearance data is locked. Contact admin to unlock.');
        }
      }
      
      const updateData = {
        allocatedAmount: clearanceData.allocatedAmount ? parseFloat(clearanceData.allocatedAmount) : null,
        uoNumber: clearanceData.uoNumber ? clearanceData.uoNumber.trim() : null,
        clearanceDate: clearanceData.clearanceDate ? new Date(clearanceData.clearanceDate) : null,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };
      
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === null || updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      await updateDoc(projectRef, updateData);
      console.log(`Session project clearance updated for project ${projectId}`);
      return { success: true, data: updateData };
    } catch (error) {
      console.error('Error updating session project clearance:', error);
      throw new Error(error.message || 'Failed to update session project clearance');
    }
  },

  // Get session projects for clearance
  async getSessionProjectsForClearance(financialYear = null) {
    try {
      let sessionQuery = collection(db, SESSION_PROJECTS_COLLECTION);
      const constraints = [];
      
      if (financialYear) constraints.push(where('financialYear', '==', financialYear));
      constraints.push(orderBy('department', 'asc'));
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) sessionQuery = query(sessionQuery, ...constraints);
      
      const querySnapshot = await getDocs(sessionQuery);
      const sessionProjects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sessionProjects.push({
          id: doc.id,
          projectName: data.projectName,
          department: data.department,
          sector: data.sector || 'Unknown',
          financialRequirement: data.amount,
          financialYear: data.financialYear,
          clearanceType: data.clearanceType,
          allocatedAmount: data.allocatedAmount || null,
          uoNumber: data.uoNumber || null,
          clearanceDate: data.clearanceDate?.toDate() || null,
          source: data.source || '',
          projectStatus: data.projectStatus || 'new_project',
          
          // Project locks
          isProjectLocked: data.isProjectLocked || false,
          projectLockedAt: data.projectLockedAt?.toDate() || null,
          projectLockedBy: data.projectLockedBy || null,
          projectLockedByName: data.projectLockedByName || null,
          
          // Clearance locks
          isClearanceLocked: data.isClearanceLocked || false,
          clearanceLockedAt: data.clearanceLockedAt?.toDate() || null,
          clearanceLockedBy: data.clearanceLockedBy || null,
          clearanceLockedByName: data.clearanceLockedByName || null,
          
          createdBy: data.createdBy || null,
          updatedBy: data.updatedBy || null,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });
      
      return { success: true, data: sessionProjects };
    } catch (error) {
      console.error('Error fetching session projects for clearance:', error);
      throw new Error('Failed to load session projects for clearance: ' + error.message);
    }
  },

  // Get session descriptive data
  async getSessionDescriptiveData(financialYear = null, department = null) {
    try {
      const sessionProjectsResult = await this.getSessionProjects(financialYear, department);
      if (!sessionProjectsResult.success) {
        throw new Error('Failed to fetch session projects');
      }
      
      let projects = sessionProjectsResult.data;
      projects.sort((a, b) => {
        if (a.department < b.department) return -1;
        if (a.department > b.department) return 1;
        if (a.projectName < b.projectName) return -1;
        if (a.projectName > b.projectName) return 1;
        return 0;
      });
      
      const totals = {
        totalFinancialRequirement: projects.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
        totalStatePlan: projects.filter(p => p.clearanceType === 'state_plan').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
        totalNegotiatedLoan: projects.filter(p => p.clearanceType === 'negotiated_loan').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
        totalProjects: projects.length,
        totalNewProjects: projects.filter(p => p.projectStatus === 'new_project').length,
        totalOngoingProjects: projects.filter(p => p.projectStatus === 'ongoing_project').length,
        newProjectsAmount: projects.filter(p => p.projectStatus === 'new_project').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
        ongoingProjectsAmount: projects.filter(p => p.projectStatus === 'ongoing_project').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
      };
      
      return {
        success: true,
        data: {
          projects,
          totals,
          department: department || 'All Departments',
          financialYear: financialYear || 'All Years'
        }
      };
    } catch (error) {
      console.error('Error getting session descriptive data:', error);
      throw new Error('Failed to load session descriptive data: ' + error.message);
    }
  },

  // Get session projects with allocations
  async getSessionProjectsWithAllocations(financialYear = null, sector = null) {
    try {
      let sessionQuery = collection(db, SESSION_PROJECTS_COLLECTION);
      const constraints = [];
      
      if (financialYear) constraints.push(where('financialYear', '==', financialYear));
      constraints.push(orderBy('department', 'asc'));
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) sessionQuery = query(sessionQuery, ...constraints);
      
      const querySnapshot = await getDocs(sessionQuery);
      const sessionProjects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sessionProjects.push({
          id: doc.id,
          department: data.department,
          sector: data.sector || 'Unknown',
          projectName: data.projectName,
          amount: data.amount || 0,
          clearanceType: data.clearanceType,
          allocatedAmount: data.allocatedAmount || 0,
          uoNumber: data.uoNumber || '',
          clearanceDate: data.clearanceDate?.toDate() || null,
          financialYear: data.financialYear,
          source: data.source || '',
          projectStatus: data.projectStatus || 'new_project',
          
          // Project locks
          isProjectLocked: data.isProjectLocked || false,
          
          // Clearance locks
          isClearanceLocked: data.isClearanceLocked || false,
          
          createdBy: data.createdBy || null,
          updatedBy: data.updatedBy || null,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });
      
      let filteredProjects = sessionProjects;
      if (sector) filteredProjects = sessionProjects.filter(project => project.sector === sector);
      
      const departmentTotals = this.calculateDepartmentAllocations(filteredProjects);
      const sectorTotals = this.calculateSectorAllocations(departmentTotals);
      
      return {
        success: true,
        data: {
          departments: departmentTotals,
          sectorTotals,
          projects: filteredProjects,
          totalProjects: filteredProjects.length
        }
      };
    } catch (error) {
      console.error('Error fetching session projects with allocations:', error);
      throw new Error('Failed to load session projects with allocations: ' + error.message);
    }
  },

  // Calculate department allocations
  calculateDepartmentAllocations(sessionProjects) {
    const departmentTotals = {};
    
    sessionProjects.forEach(project => {
      if (!project.department) return;
      
      if (!departmentTotals[project.department]) {
        departmentTotals[project.department] = {
          department: project.department,
          sector: project.sector || 'Unknown',
          approvedTotal: 0,
          approvedStatePlan: 0,
          approvedNegotiatedLoan: 0,
          clearanceTotal: 0,
          clearanceStatePlan: 0,
          clearanceNegotiatedLoan: 0
        };
      }
      
      const dept = departmentTotals[project.department];
      const proposedAmount = parseFloat(project.amount) || 0;
      const allocatedAmount = parseFloat(project.allocatedAmount) || 0;
      
      dept.approvedTotal += proposedAmount;
      
      if (project.clearanceType === 'state_plan') {
        dept.approvedStatePlan += proposedAmount;
      } else if (project.clearanceType === 'negotiated_loan') {
        dept.approvedNegotiatedLoan += proposedAmount;
      }
      
      if (allocatedAmount > 0) {
        dept.clearanceTotal += allocatedAmount;
        
        if (project.clearanceType === 'state_plan') {
          dept.clearanceStatePlan += allocatedAmount;
        } else if (project.clearanceType === 'negotiated_loan') {
          dept.clearanceNegotiatedLoan += allocatedAmount;
        }
      }
    });
    
    return Object.values(departmentTotals);
  },

  // Calculate sector allocations
  calculateSectorAllocations(departmentData) {
    const sectorTotals = {};
    
    departmentData.forEach(dept => {
      if (!sectorTotals[dept.sector]) {
        sectorTotals[dept.sector] = {
          sector: dept.sector,
          approvedTotal: 0,
          approvedStatePlan: 0,
          approvedNegotiatedLoan: 0,
          clearanceTotal: 0,
          clearanceStatePlan: 0,
          clearanceNegotiatedLoan: 0,
          departmentCount: 0
        };
      }
      
      sectorTotals[dept.sector].approvedTotal += dept.approvedTotal || 0;
      sectorTotals[dept.sector].approvedStatePlan += dept.approvedStatePlan || 0;
      sectorTotals[dept.sector].approvedNegotiatedLoan += dept.approvedNegotiatedLoan || 0;
      sectorTotals[dept.sector].clearanceTotal += dept.clearanceTotal || 0;
      sectorTotals[dept.sector].clearanceStatePlan += dept.clearanceStatePlan || 0;
      sectorTotals[dept.sector].clearanceNegotiatedLoan += dept.clearanceNegotiatedLoan || 0;
      sectorTotals[dept.sector].departmentCount += 1;
    });
    
    return sectorTotals;
  }
};

export default sessionService;